import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table } from "../blocks.js";
import { QUOTED } from "../thresholds.js";
import type { WikiTopic } from "../types.js";

export const emaCross: WikiTopic = {
  slug: "ema-cross",
  title: "EMA cross",
  summary: "The fast EMA crossing the slow EMA on an hourly bucket close.",
  group: "strategies",
  horizon: "session",
  strategyKey: "ema_cross",
  sections: [
    {
      heading: "What it looks for",
      code: "ema_cross_bullish / ema_cross_bearish",
      blocks: [
        p(
          <>
            An EMA-<Datum>{QUOTED.EMA_CROSS_FAST}</Datum> against an EMA-
            <Datum>{QUOTED.EMA_CROSS_SLOW}</Datum>, both on hourly closes. Bullish when the fast
            line sat at or below the slow one on the previous bucket and sits above it now. Bearish
            is the mirror.
          </>,
        ),
        p(
          <>
            It needs <Datum>{QUOTED.EMA_CROSS_MIN_BARS}</Datum> buckets of history, which is five
            times the slow period. That is the point where the recursion has forgotten its seed, so
            the first value the cross is decided on is a real EMA rather than an artefact of
            whatever the series was seeded with.
          </>,
        ),
      ],
    },
    {
      heading: "The gates",
      blocks: [
        table(
          "EMA cross gates",
          ["Gate", "Value"],
          [
            ["Fast period", <Datum key="f">{QUOTED.EMA_CROSS_FAST}</Datum>],
            ["Slow period", <Datum key="s">{QUOTED.EMA_CROSS_SLOW}</Datum>],
            ["Minimum history", <Datum key="b">{QUOTED.EMA_CROSS_MIN_BARS} buckets</Datum>],
            ["Series", "Hourly closes"],
          ],
        ),
        p(
          <>
            There is no cooldown in the code. Direction lives in the signal type rather than the
            payload, and <Datum>signals</Datum> carries a unique index on exchange, symbol, signal
            type, and session date. That makes the index a once-per-symbol-per-direction-per-day
            cooldown the database enforces: a genuine same-day reversal still fires because it is a
            different type, and the third flip in a chop does not.
          </>,
        ),
      ],
    },
    {
      heading: "What the row carries",
      blocks: [
        p(
          <>
            Magnitude is the gap between the two lines divided by the hourly ATR, so it reads as a
            distance in ATR. The EMA/VWAP cross scales its magnitude the same way, which is what
            makes the two comparable in a validation distribution.
          </>,
        ),
        p(
          <>
            The stop is the signal bar's low on a long and its high on a short. The target is twice
            that distance the other way, a flat 2:1. That arithmetic came over from the reference
            unchanged, and nothing in the system acts on the levels.
          </>,
        ),
      ],
    },
    {
      heading: "Caveat",
      blocks: [
        note(
          <>
            A symbol whose ATR window has a null or zero true range returns nothing rather than
            throwing. That symbol did not trade, and the runner counts a throw as a failure, so
            throwing would make a quiet symbol look like a broken strategy.
          </>,
        ),
      ],
    },
  ],
};
